import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { setScopedAccount } from '../../services/authorizationService';
import accountService from '../../services/accountService';
import { 
  Plus, 
  MagnifyingGlass, 
  Building, 
  Pencil,
  Trash,
  X
} from '@phosphor-icons/react';

export default function Accounts() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    name: '',
    description: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    status: 'active'
  });

  // Load accounts
  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const accountsData = await accountService.getAllAccounts();
      setAccounts(accountsData);
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load accounts on mount
  useEffect(() => {
    if (currentUser?.uid) {
      loadAccounts();
    }
  }, [currentUser, loadAccounts]);

  // Handle account form submission
  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await accountService.updateAccount(editingAccount.id, accountForm);
        setAccounts(prev => prev.map(account => 
          account.id === editingAccount.id ? { ...account, ...accountForm } : account
        ));
      } else {
        const newAccount = await accountService.createAccount(accountForm);
        setAccounts(prev => [...prev, newAccount]);
      }

      resetAccountForm();
      setShowAccountModal(false);
    } catch (error) {
      console.error('Error saving account:', error);
      alert('Failed to save account. Please try again.');
    }
  };

  // Reset account form
  const resetAccountForm = () => {
    setAccountForm({
      name: '',
      description: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      status: 'active'
    });
    setEditingAccount(null);
  };

  // Handle account deletion
  const handleAccountDelete = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) return;

    try {
      await accountService.deleteAccount(accountId);
      setAccounts(prev => prev.filter(account => account.id !== accountId));
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
    }
  };

  // Handle edit account
  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setAccountForm({
      name: account.name || '',
      description: account.description || '',
      contactEmail: account.contactEmail || '',
      contactPhone: account.contactPhone || '',
      address: account.address || '',
      city: account.city || '',
      state: account.state || '',
      zipCode: account.zipCode || '',
      status: account.status || 'active'
    });
    setShowAccountModal(true);
  };

  // Handle account status toggle
  const handleStatusToggle = async (accountId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await accountService.updateAccount(accountId, { status: newStatus });
      setAccounts(prev => prev.map(account => 
        account.id === accountId ? { ...account, status: newStatus } : account
      ));
    } catch (error) {
      console.error('Error updating account status:', error);
      alert('Failed to update account status. Please try again.');
    }
  };

  // Handle viewing an account (switch to it)
  const handleViewAccount = async (account) => {
    try {
      await setScopedAccount(account.id, account.name);
      // Navigate to the Overview page for this account
      navigate('/dashboard');
    } catch (error) {
      console.error('Error switching to account:', error);
      alert('Failed to switch to account. Please try again.');
    }
  };

  // Filter accounts
  const filteredAccounts = accounts.filter(account => {
    const name = account.name || '';
    const contactEmail = account.contactEmail || '';
    const city = account.city || '';

    return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           contactEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
           city.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Client Accounts</h2>
          <p className="text-gray-600">Manage client accounts and access</p>
        </div>
        <button 
          onClick={() => {
            resetAccountForm();
            setShowAccountModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Account</span>
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input 
            type="text" 
            placeholder="Search accounts..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
          />
        </div>
      </div>

      {/* Accounts Grid */}
      <div className="bg-white rounded-lg shadow">
        {filteredAccounts.length === 0 ? (
          <div className="text-center py-12">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No accounts found</h3>
            <p className="text-gray-500 mb-4">
              {accounts.length === 0 
                ? 'Add your first client account to get started' 
                : 'Try adjusting your search'
              }
            </p>
            <button 
              onClick={() => {
                resetAccountForm();
                setShowAccountModal(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Account
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {filteredAccounts.map((account) => (
              <div key={account.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Building className="w-8 h-8 text-blue-500" />
                    <div>
                      <h3 className="font-medium text-gray-900">{account.name || 'Unnamed Account'}</h3>
                      <p className="text-sm text-gray-500">
                        {account.city || 'Unknown City'}{account.state ? `, ${account.state}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleEditAccount(account)}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Edit Account"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleAccountDelete(account.id)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Delete Account"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Account Details */}
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  {account.description && (
                    <p className="line-clamp-2">{account.description}</p>
                  )}
                  {account.contactEmail && (
                    <p className="flex items-center">
                      <span className="w-4 h-4 mr-2">📧</span>
                      {account.contactEmail}
                    </p>
                  )}
                  {account.contactPhone && (
                    <p className="flex items-center">
                      <span className="w-4 h-4 mr-2">📞</span>
                      {account.contactPhone}
                    </p>
                  )}
                  {account.address && (
                    <p className="flex items-center">
                      <span className="w-4 h-4 mr-2">📍</span>
                      {account.address}
                    </p>
                  )}
                </div>

                {/* Status and Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      account.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {account.status === 'active' ? 'Active' : 'Suspended'}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleStatusToggle(account.id, account.status)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        account.status === 'active'
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {account.status === 'active' ? 'Suspend' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleViewAccount(account)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-200 transition-colors"
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Account Modal */}
      {showAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingAccount ? 'Edit Account' : 'Add Account'}
              </h3>
              <button 
                onClick={() => {
                  setShowAccountModal(false);
                  resetAccountForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAccountSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Name *</label>
                  <input
                    type="text"
                    required
                    value={accountForm.name}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Client Practice Name"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={accountForm.description}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief description of the practice..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                  <input
                    type="email"
                    value={accountForm.contactEmail}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    value={accountForm.contactPhone}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={accountForm.address}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    value={accountForm.city}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="New York"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    value={accountForm.state}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="NY"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                  <input
                    type="text"
                    value={accountForm.zipCode}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, zipCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="10001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={accountForm.status}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAccountModal(false);
                    resetAccountForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editingAccount ? 'Update Account' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
